/**
 * Seed the blog listing pages from prerendered data (audit H7).
 *
 * /blog and its four category pages fetched their posts in the BROWSER, so to a
 * crawler they were empty shells: five listing pages that advertised nothing they
 * listed. The build now injects the first page of post summaries, and these helpers
 * turn that payload into what the existing components already render.
 *
 * Two deliberate shapes:
 *
 *  - `body` is EMPTY. The payload omits it on purpose — eleven article bodies would
 *    several-fold the weight of /blog — and the only thing the card derived from it,
 *    read-time, is precomputed at build by the same util. Use `readTimeBySlug` to
 *    supply it; never compute read-time from a seeded post's body.
 *
 *  - `published_at` is rehydrated from ISO-8601, because the payload is JSON and JSON
 *    has no date type. An unparseable date yields no post rather than an Invalid Date
 *    rendering as "NaN" on a live page.
 *
 * The seed is NOT a first-paint device that a fetch later corrects — when it is adopted,
 * the page does not fetch at all, because the deploy is what publishes a post and the
 * deployed list is therefore the truth. That makes route-scoping load-bearing rather
 * than cosmetic: callers must adopt a payload only when `blogListingCategory` matches
 * their own route (see its docblock in ./ssGData), since the `__SSG_DATA__` tag survives
 * client-side navigation and would otherwise hand one route another's posts with no
 * fetch to correct it.
 */
import type { SsgBlogPostSummary, SsgBlogPostFull } from './ssGData';
import type { HydratedBlogPost } from '../src/types/blog';
import { pickBlogTags } from './blogTagPayload';
import { isBlogCategory } from '../src/constants/blog';

/**
 * Adopt a full-post seed for /blog/:category/:slug (UI-06) — or refuse it.
 *
 * Same discipline as the listing seed below, with the same load-bearing route
 * scoping: the __SSG_DATA__ tag survives client-side navigation, so the seed is
 * adopted ONLY when category AND slug both match the caller's route. A refused
 * seed simply means the page fetches, exactly as before UI-06.
 *
 * `body` arrives BUILD-SANITIZED (see SsgBlogPostFull's docblock); this helper
 * does not re-sanitize — the render path (sanitizeBlogBody) still runs on it in
 * the browser, idempotently.
 */
export function toSeededFullPost(
    seed: SsgBlogPostFull | undefined,
    category: string | null | undefined,
    slug: string | undefined,
): HydratedBlogPost | null {
    if (!seed || !category || !slug) return null;
    if (seed.category !== category || seed.slug !== slug) return null;
    if (!isBlogCategory(seed.category)) return null;
    const published = new Date(seed.published_at);
    if (Number.isNaN(published.getTime())) return null;
    const updated = seed.updated_at ? new Date(seed.updated_at) : null;
    return {
        id: seed.slug,
        slug: seed.slug,
        title: seed.title,
        category: seed.category,
        excerpt: seed.excerpt,
        published_at: published,
        body: seed.body,
        status: 'published',
        ...(updated && !Number.isNaN(updated.getTime()) ? { updated_at: updated } : {}),
        ...(seed.featured_image_url ? { featured_image_url: seed.featured_image_url } : {}),
        ...(seed.seo_title ? { seo_title: seed.seo_title } : {}),
        // Topic tags (item 16). Re-validated HERE rather than trusted from the payload:
        // the seed arrives as JSON `string[]` while HydratedBlogPost types these as
        // literal unions, and the `as HydratedBlogPost` cast that used to end this
        // function would have let any string through unchecked. Removing that cast is
        // only safe because pickBlogTags narrows every value against the vocabulary.
        ...pickBlogTags(seed),
    };
}

/**
 * Convert prerendered summaries into the post shape the listing components take.
 * Entries with an unknown category or an unparseable date are dropped: a listing
 * page showing a broken row is worse than showing one fewer post for the
 * milliseconds before the live fetch lands.
 */
export function toSeededPosts(summaries: readonly SsgBlogPostSummary[] | undefined): HydratedBlogPost[] {
    if (!summaries?.length) return [];
    return summaries.flatMap((s) => {
        if (!isBlogCategory(s.category)) return [];
        const published = new Date(s.published_at);
        if (Number.isNaN(published.getTime())) return [];
        return [{
            id: s.slug,
            slug: s.slug,
            title: s.title,
            category: s.category,
            excerpt: s.excerpt,
            published_at: published,
            // Intentionally empty — see the module docblock.
            body: '',
            status: 'published',
            ...(s.featured_image_url ? { featured_image_url: s.featured_image_url } : {}),
        } as HydratedBlogPost];
    });
}

/**
 * slug → precomputed read-time, for passing to PostCard.
 *
 * Kept after the live fetch replaces the seed: the value came from the same util
 * over the same body, so it stays correct and the card never flickers its read-time.
 */
export function readTimeBySlug(summaries: readonly SsgBlogPostSummary[] | undefined): Record<string, number> {
    const out: Record<string, number> = {};
    for (const s of summaries ?? []) out[s.slug] = s.readTime;
    return out;
}
